import { View, Text, styled } from 'tamagui'
import { ArrowRight, Lock, UserPlus, Hash, Pin, Settings2 } from '@tamagui/lucide-icons'
import { useState, useCallback, useRef } from 'react'
import { useAppStore } from '@/store'
import { Avatar } from '../../common'
import { MessageActions } from './MessageActions'
import { MessageContent } from './MessageContent'
import { MessageAttachment } from './MessageAttachment'
import { PollMessage } from './PollMessage'
import { ReactionPopover } from './ReactionPopover'
import { messages, users } from '@/mocks'
import type { Message, User } from '@/types'

const MessageRow = styled(View, {
  flexDirection: 'row',
  paddingVertical: 2,
  paddingHorizontal: 8,
  borderRadius: '$borderRadiusSm',
  position: 'relative',
  gap: 12,
  hoverStyle: {
    backgroundColor: '$sidebar',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const AvatarColumn = styled(View, {
  width: 40,
  flexShrink: 0,
  paddingTop: 2,
})

const ContentColumn = styled(View, {
  flex: 1,
  minWidth: 0,
})

const AuthorRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: 8,
  marginBottom: 2,
})

const AuthorName = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 15,
  cursor: 'pointer',
  hoverStyle: {
    textDecorationLine: 'underline',
  },
})

const BotBadge = styled(View, {
  backgroundColor: '$accent',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 4,
  paddingVertical: 1,
})

const BotText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 10,
  fontWeight: '700',
  color: '$surface',
})

const Timestamp = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
})

const EditedLabel = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 10,
  color: '$color8',
})

const GroupedTimestamp = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: 'transparent',
  width: 40,
  textAlign: 'center',
  paddingTop: 4,
  // @ts-expect-error -- web transition
  style: { transition: 'color 100ms ease' },
})

const ReactionsRow = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 4,
})

const Reaction = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  paddingHorizontal: 6,
  paddingVertical: 2,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$sidebar',
  borderWidth: 1,
  borderColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: {
    borderColor: '$accent',
    backgroundColor: '$accentLighter',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'border-color 150ms ease, background-color 150ms ease' },
})

const ReactionCount = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

const ThreadButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: '$borderRadiusSm',
  marginTop: 4,
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebar',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const ThreadText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$accent',
  fontWeight: '600',
})

// Reply quote styles
const ReplyQuote = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  paddingVertical: 4,
  paddingHorizontal: 8,
  marginBottom: 4,
  borderLeftWidth: 2,
  borderLeftColor: '$accent',
  cursor: 'pointer',
  borderRadius: '$borderRadiusSm',
  hoverStyle: {
    backgroundColor: '$sidebar',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const ReplyAuthor = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
  color: '$accent',
})

const ReplyPreview = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$color8',
  flex: 1,
  // @ts-expect-error -- web text overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

// Edit mode styles
const EditContainer = styled(View, {
  gap: 8,
})

const EditTextAreaWrapper = styled(View, {
  borderWidth: 1,
  borderColor: '$accent',
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$sidebar',
  overflow: 'hidden',
})

const EditActions = styled(View, {
  flexDirection: 'row',
  gap: 8,
  alignItems: 'center',
})

const EditHint = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  color: '$color8',
  flex: 1,
})

const EditButton = styled(View, {
  paddingHorizontal: 12,
  paddingVertical: 4,
  borderRadius: '$borderRadiusSm',
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const SaveButton = styled(EditButton, {
  backgroundColor: '$accent',
  hoverStyle: {
    opacity: 0.9,
  },
})

const CancelButton = styled(EditButton, {
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
})

const ButtonText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  fontWeight: '600',
})

// Blocked message styles
const BlockedMessage = styled(View, {
  paddingVertical: 4,
  cursor: 'pointer',
})

const BlockedText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  color: '$color8',
  fontStyle: 'italic',
})

// System message styles
const SystemMessageRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  paddingVertical: 6,
  paddingHorizontal: 16,
})

const SystemMessageText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$color8',
  fontStyle: 'italic',
})

const SystemTimestamp = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 10,
  color: '$color8',
})

// Edit history popover
const EditHistoryPopover = styled(View, {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  width: 300,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 10,
  gap: 8,
  zIndex: 1000,
  marginBottom: 4,
})

const EditHistoryEntry = styled(View, {
  gap: 2,
  paddingBottom: 6,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const EditHistoryTimestamp = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 10,
  color: '$color8',
})

const EditHistoryContent = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$sidebarMuted',
  lineHeight: 18,
})

// Role color dot
const RoleColorDot = styled(View, {
  width: 8,
  height: 8,
  borderRadius: '$borderRadiusFull',
  borderWidth: 2,
  borderColor: '$surface',
  position: 'absolute',
  bottom: 0,
  right: 0,
})

function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h = hours % 12 || 12
  return `${h}:${minutes} ${ampm}`
}

function findMessageById(messageId: string): Message | undefined {
  for (const channelMessages of Object.values(messages)) {
    const found = channelMessages.find((m) => m.id === messageId)
    if (found) return found
  }
  return undefined
}

interface MessageItemProps {
  message: Message
  user?: User
  showAvatar?: boolean
  showActions?: boolean
}

export function MessageItem({ message, user, showAvatar = true, showActions = true }: MessageItemProps) {
  const [hovered, setHovered] = useState(false)
  const [activeReactions, setActiveReactions] = useState<Set<string>>(new Set())
  const [blockedRevealed, setBlockedRevealed] = useState(false)
  const [editValue, setEditValue] = useState(message.content)
  const [hoveredReaction, setHoveredReaction] = useState<string | null>(null)
  const [showEditHistory, setShowEditHistory] = useState(false)
  const reactionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const editRef = useRef<HTMLTextAreaElement>(null)
  const {
    openThread,
    openProfileModal,
    openContextMenu,
    editingMessageId,
    setEditingMessageId,
    compactMode,
  } = useAppStore()

  const isEditing = editingMessageId === message.id

  const toggleReaction = (emoji: string) => {
    setActiveReactions(prev => {
      const next = new Set(prev)
      if (next.has(emoji)) next.delete(emoji)
      else next.add(emoji)
      return next
    })
  }

  const handleContextMenu = useCallback((e: any) => {
    // Prevent native context menu
    const nativeEvent = e.nativeEvent || e
    if (nativeEvent.preventDefault) nativeEvent.preventDefault()
    const clientX = nativeEvent.clientX ?? nativeEvent.pageX ?? 0
    const clientY = nativeEvent.clientY ?? nativeEvent.pageY ?? 0
    openContextMenu('message', message.id, clientX, clientY)
  }, [message.id, openContextMenu])

  const handleSaveEdit = () => {
    // In a real app, this would call an API
    // For mock, update local data
    message.content = editValue
    message.editedAt = Date.now()
    setEditingMessageId(null)
  }

  const handleCancelEdit = () => {
    setEditValue(message.content)
    setEditingMessageId(null)
  }

  if (!user) return null

  // System message rendering
  if (message.type === 'system') {
    const systemIcons = {
      member_join: UserPlus,
      channel_create: Hash,
      pin: Pin,
      server_update: Settings2,
    }
    const Icon = message.systemEvent ? systemIcons[message.systemEvent] : Hash
    return (
      <SystemMessageRow>
        <Icon size={14} color="$color8" />
        <SystemMessageText>{message.content}</SystemMessageText>
        <SystemTimestamp>{formatTime(message.timestamp)}</SystemTimestamp>
      </SystemMessageRow>
    )
  }

  // Blocked user handling
  if (user.isBlocked && !blockedRevealed) {
    return (
      <MessageRow paddingTop={showAvatar ? 8 : 2}>
        <AvatarColumn />
        <ContentColumn>
          <BlockedMessage onPress={() => setBlockedRevealed(true)}>
            <BlockedText>[Blocked message — click to reveal]</BlockedText>
          </BlockedMessage>
        </ContentColumn>
      </MessageRow>
    )
  }

  // Find reply target
  const replyMessage = message.replyToId ? findMessageById(message.replyToId) : undefined
  const replyUser = replyMessage ? users[replyMessage.authorId] : undefined

  return (
    <MessageRow
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      paddingTop={showAvatar ? (compactMode ? 4 : 8) : 2}
      // @ts-expect-error -- web onContextMenu
      onContextMenu={handleContextMenu}
    >
      {/* Avatar or grouped timestamp */}
      <AvatarColumn {...(compactMode && { width: 24 })}>
        {showAvatar ? (
          <View position="relative" width={compactMode ? 24 : 40}>
            <Avatar
              name={user.displayName}
              color={user.roleColor || '#2A3142'}
              size={compactMode ? 'sm' : 'lg'}
              showStatus={false}
            />
            {user.roleColor && (
              <RoleColorDot backgroundColor={user.roleColor} />
            )}
          </View>
        ) : (
          <GroupedTimestamp
            color={hovered ? '$color8' : 'transparent'}
          >
            {formatTime(message.timestamp)}
          </GroupedTimestamp>
        )}
      </AvatarColumn>

      {/* Message content */}
      <ContentColumn>
        {/* Reply quote */}
        {replyMessage && replyUser && (
          <ReplyQuote onPress={() => {
            // Scroll to referenced message (mock — just log)
          }}>
            <Avatar
              name={replyUser.displayName}
              color={replyUser.roleColor || '#2A3142'}
              size="sm"
              showStatus={false}
            />
            <ReplyAuthor>{replyUser.displayName}</ReplyAuthor>
            <ReplyPreview>{replyMessage.content}</ReplyPreview>
          </ReplyQuote>
        )}

        {showAvatar && (
          <AuthorRow>
            <AuthorName
              color={user.roleColor || '$surfaceForeground'}
              onPress={() => openProfileModal(message.authorId)}
            >
              {user.displayName}
            </AuthorName>
            {user.isBot && (
              <BotBadge>
                <BotText>BOT</BotText>
              </BotBadge>
            )}
            <Lock size={10} color="$accent" />
            <Timestamp>{formatTime(message.timestamp)}</Timestamp>
            {message.editedAt && (
              <View position="relative">
                <EditedLabel
                  cursor={message.editHistory ? 'pointer' : 'default'}
                  hoverStyle={message.editHistory ? { textDecorationLine: 'underline' } : undefined}
                  onPress={() => message.editHistory && setShowEditHistory(!showEditHistory)}
                >
                  (edited)
                </EditedLabel>
                {showEditHistory && message.editHistory && (
                  <EditHistoryPopover>
                    <Text fontFamily="$fontBody" fontSize={11} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5}>
                      Edit History
                    </Text>
                    {message.editHistory.map((entry, idx) => (
                      <EditHistoryEntry key={idx}>
                        <EditHistoryTimestamp>{formatTime(entry.editedAt)}</EditHistoryTimestamp>
                        <EditHistoryContent>{entry.content}</EditHistoryContent>
                      </EditHistoryEntry>
                    ))}
                  </EditHistoryPopover>
                )}
              </View>
            )}
          </AuthorRow>
        )}

        {/* Inline edit mode or message content */}
        {isEditing ? (
          <EditContainer>
            <EditTextAreaWrapper>
              <textarea
                ref={editRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelEdit()
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSaveEdit()
                  }
                }}
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 14,
                  lineHeight: '22px',
                  color: '#C5CBD9',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  width: '100%',
                  minHeight: 60,
                  padding: '8px 12px',
                }}
              />
            </EditTextAreaWrapper>
            <EditActions>
              <EditHint>Escape to cancel, Enter to save</EditHint>
              <CancelButton onPress={handleCancelEdit}>
                <ButtonText color="$sidebarMuted">Cancel</ButtonText>
              </CancelButton>
              <SaveButton onPress={handleSaveEdit}>
                <ButtonText color="$surface">Save</ButtonText>
              </SaveButton>
            </EditActions>
          </EditContainer>
        ) : (
          <>
            <MessageContent content={message.content} />

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <MessageAttachment attachments={message.attachments} />
            )}

            {/* Poll */}
            {message.poll && (
              <PollMessage poll={message.poll} />
            )}
          </>
        )}

        {/* Reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <ReactionsRow>
            {message.reactions.map((reaction) => {
              const isActive = activeReactions.has(reaction.emoji)
              const count = reaction.userIds.length + (isActive ? 1 : 0)
              return (
                <View key={reaction.emoji} position="relative">
                  <Reaction
                    ref={(el: any) => { reactionRefs.current[reaction.emoji] = el }}
                    onPress={() => toggleReaction(reaction.emoji)}
                    onMouseEnter={() => setHoveredReaction(reaction.emoji)}
                    onMouseLeave={() => setHoveredReaction(null)}
                    borderColor={isActive ? '$accent' : '$borderDefault'}
                    backgroundColor={isActive ? '$accentLighter' : '$sidebar'}
                  >
                    <Text fontSize={14}>{reaction.emoji}</Text>
                    <ReactionCount color={isActive ? '$accent' : '$sidebarMuted'}>{count}</ReactionCount>
                  </Reaction>
                  {hoveredReaction === reaction.emoji && (
                    <View position="absolute" bottom="100%" left={0} marginBottom={4} zIndex={1000}>
                      <ReactionPopover emoji={reaction.emoji} userIds={reaction.userIds} />
                    </View>
                  )}
                </View>
              )
            })}
          </ReactionsRow>
        )}

        {/* Thread indicator */}
        {message.threadCount && message.threadCount > 0 && (
          <ThreadButton onPress={() => openThread(message.id)}>
            <ThreadText>
              {message.threadCount} {message.threadCount === 1 ? 'reply' : 'replies'}
            </ThreadText>
            <ArrowRight size={12} color="$color8" />
          </ThreadButton>
        )}
      </ContentColumn>

      {/* Hover actions */}
      {showActions && hovered && !isEditing && (
        <View position="absolute" top={-12} right={8}>
          <MessageActions messageId={message.id} hasThread={(message.threadCount || 0) > 0} />
        </View>
      )}
    </MessageRow>
  )
}
